import z from 'zod'
import env from '@/config/env'
import useDrivePicker from '@liftos-js/react-google-drive-picker'
import { trpc } from '@/config/trpc'
import { GoogleAuthButton } from '../auth/auth-google'
import { Button } from './shadcn/button'
import { useEffect, useMemo, useState } from 'react'
import { GoogleLogo } from './icons/GoogleLogo'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './shadcn/dropdown-menu'
import { CheckIcon, ChevronDown, PlusIcon, X } from 'lucide-react'
import { toast } from 'sonner'
import { zAssetPrefixInputs } from '@backend/routes/trpc/asset/lib'
import { SCOPE as DRIVE_SCOPE } from '@/lib/auth/drive'

interface DriveFilePickerProps {
  destination: z.infer<typeof zAssetPrefixInputs>
  onTransferStart: () => void
  onTansferComplete: (
    files: { url: string; name: string; type: string; size: number; key: string }[],
  ) => void
  disabled?: boolean
}

export const DriveFilePicker = ({
  destination,
  onTransferStart,
  onTansferComplete,
  disabled,
}: DriveFilePickerProps) => {
  const {
    data: driveAuth,
    isLoading: isAuthLoading,
    refetch,
  } = trpc.service.isAuthenticated.useQuery({ service: 'google_drive' })

  const [isFilesLoading, setIsFilesLoading] = useState(false)
  const [openPicker] = useDrivePicker()

  const { mutateAsync: getDriveToken } = trpc.service.getAccessToken.useMutation()
  const { mutateAsync: driveToS3 } = trpc.service.googleDrive.uploadToS3.useMutation()
  const { mutateAsync: signOutService } = trpc.service.signOut.useMutation()

  const accounts = useMemo(() => driveAuth?.accounts || [], [driveAuth])
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0) {
      setSelectedAccountId(accounts[0]?.serviceUserId)
    }
  }, [accounts, selectedAccountId])

  const handleDisconnectAccount = async (
    accountId: string,
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (e) e.stopPropagation()
    await signOutService({ service: 'google_drive', serviceUserId: accountId })
    const { data } = await refetch()
    const next = data?.accounts?.[0]?.serviceUserId
    setSelectedAccountId(next)
  }

  const handleOpenPicker = async () => {
    if (!selectedAccountId) {
      alert('Please connect Google Drive first')
      return
    }

    try {
      const { accessToken } = await getDriveToken({
        service: 'google_drive',
        serviceUserId: selectedAccountId,
      })

      openPicker({
        clientId: env.VITE_GOOGLE_CLIENT_ID,
        developerKey: env.VITE_GOOGLE_SERVICES_API_KEY,
        token: accessToken,

        multiselect: true,
        supportDrives: true,
        setSelectFolderEnabled: false,

        callbackFunction: (data) => {
          if (data.action === 'cancel') {
            console.log('User cancelled picker')
          } else if (data.action === 'picked' && data.docs && data.docs.length > 0) {
            console.log('User picked files:', data.docs)
            handleSelectedFiles(data.docs)
          }
        },
      })
    } catch (error) {
      console.error('Failed to open picker:', error)
    }
  }

  const handleSelectedFiles = async (
    selectedFiles: { id: string; name: string; mimeType?: string; sizeBytes?: number }[],
  ) => {
    await copyToS3Strategy(selectedFiles)
  }

  const copyToS3Strategy = async (selectedFiles: { id: string; name: string }[]) => {
    if (!selectedAccountId) {
      toast.error('Please select a Google account')
      return
    }

    setIsFilesLoading(true)
    onTransferStart()
    try {
      const fileIds = selectedFiles.map((file) => file.id)
      const { uploaded, failed } = await driveToS3({
        fileIds,
        destinationPrefix: destination,
        serviceUserId: selectedAccountId,
      })

      if (failed.length > 0) {
        console.error('Failed to copy files:', failed)
        toast.error(`Failed to copy ${failed.length} files`)
      }

      onTansferComplete(uploaded)
    } catch (error) {
      console.error('Copy to S3 error:', error)
      onTansferComplete([])
    } finally {
      setIsFilesLoading(false)
    }
  }

  return (
    <div>
      {driveAuth?.isAuthenticated ? (
        <div className="flex items-center gap-0.5">
          {!selectedAccountId || accounts.length === 0 ? (
            <GoogleAuthButton
              referrerPath={window.location.pathname}
              flow="drive"
              scope={DRIVE_SCOPE}
              buttonProps={{
                children: 'Connect account',
                disabled: isAuthLoading || disabled,
                size: 'sm',
                variant: 'secondary',
              }}
            />
          ) : (
            <>
              <Button
                variant="secondary"
                size="sm"
                className="rounded-r-none"
                onClick={handleOpenPicker}
                disabled={isFilesLoading || isAuthLoading || !selectedAccountId || disabled}>
                <GoogleLogo width={'16'} height={'16'} />
                {isFilesLoading ? 'Copying...' : 'Browse Files'}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="rounded-none px-2"
                    disabled={isAuthLoading || isFilesLoading || disabled}>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {(accounts || []).map((acc) => (
                    <DropdownMenuItem
                      key={acc.serviceUserId}
                      onClick={() => setSelectedAccountId(acc.serviceUserId)}
                      className="justify-between">
                      <div className="flex items-center gap-2">
                        {selectedAccountId === acc.serviceUserId && <CheckIcon size={16} />}
                        <span className="truncate">{acc.serviceUserEmail}</span>
                      </div>
                      <button
                        onClick={(e) => void handleDisconnectAccount(acc.serviceUserId, e)}
                        className="hover:bg-hover ml-2 rounded-sm p-0.5">
                        <X className="text-muted-foreground h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <GoogleAuthButton
                referrerPath={window.location.pathname}
                flow="drive"
                scope={DRIVE_SCOPE}
                buttonProps={{
                  icon: <PlusIcon />,
                  children: '',
                  disabled: isFilesLoading || isAuthLoading || disabled,
                  size: 'icon-sm',
                  variant: 'secondary',
                  className: 'rounded-l-none flex items-center justify-center',
                }}
              />
            </>
          )}
        </div>
      ) : (
        <div>
          <GoogleAuthButton
            referrerPath={window.location.pathname}
            flow="drive"
            scope={DRIVE_SCOPE}
            buttonProps={{
              children: isAuthLoading ? 'Connecting...' : 'Connect Google Drive',
              disabled: isAuthLoading || disabled,
              size: 'sm',
              variant: 'secondary',
            }}
          />
        </div>
      )}
    </div>
  )
}
